import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Waves } from 'lucide-react';
import Footer from './Footer';
import CoachSidebar from './CoachSidebar';
import RowerSidebar from './RowerSidebar';
import AdminSidebar, { adminDrillPanelForPath } from './AdminSidebar';
import ParentSidebar from './ParentSidebar';
import { useCoachNavigation } from '../../hooks/useCoachNavigation';
import { SidebarDrillProvider, useSidebarDrill } from '../../contexts/SidebarDrillContext';
import { useAuth } from '../../contexts/AuthContext';
import NotificationsBell from '../notifications/NotificationsBell';
import MfaGraceBanner from '../auth/MfaGraceBanner';

interface AppMainProps {
  children: React.ReactNode;
  className: string;
  pathname: string;
}

/** Re-open sidebar drill after drill-back when the user interacts with main content (admin compliance routes). */
const AppMain: React.FC<AppMainProps> = ({ children, className, pathname }) => {
  const sidebarDrill = useSidebarDrill();

  const handlePointerDown = () => {
    const panel = adminDrillPanelForPath(pathname);
    if (panel) sidebarDrill?.ensureDrillPanel(panel);
  };

  return (
    <main id="app-main-scroll" className={className} onPointerDown={handlePointerDown}>
      {children}
    </main>
  );
};

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth < 768);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const collapsedBeforeBuilderRef = useRef<boolean | null>(null);
  const wasInBuilderRef = useRef(false);
  const sidebarCollapsedRef = useRef(sidebarCollapsed);
  sidebarCollapsedRef.current = sidebarCollapsed;
  const { navigateToSection, getActiveSection } = useCoachNavigation();

  const isDashboardBuilder =
    location.pathname.startsWith('/coach') &&
    new URLSearchParams(location.search).get('mode') === 'builder';

  React.useEffect(() => {
    if (!location.pathname.startsWith('/coach') || isMobileViewport) return;

    if (isDashboardBuilder) {
      if (!wasInBuilderRef.current) {
        collapsedBeforeBuilderRef.current = sidebarCollapsedRef.current;
        wasInBuilderRef.current = true;
        setSidebarCollapsed(true);
      }
      return;
    }

    if (wasInBuilderRef.current) {
      wasInBuilderRef.current = false;
      if (collapsedBeforeBuilderRef.current !== null) {
        setSidebarCollapsed(collapsedBeforeBuilderRef.current);
        collapsedBeforeBuilderRef.current = null;
      }
    }
  }, [isDashboardBuilder, isMobileViewport, location.pathname]);

  React.useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileViewport(mobile);
      if (!mobile) setMobileDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleTabChange = (tab: string, subItem?: string) => {
    navigateToSection(tab, subItem);
    if (isMobileViewport) setMobileDrawerOpen(false);
  };

  const standalonePublicFlowPaths = [
    '/rower/invitations/minor',
    '/rower/complete-signup',
    '/parent/consent',
    '/parent/reconsent',
  ] as const;
  const isStandalonePublicFlow = standalonePublicFlowPaths.includes(
    location.pathname as (typeof standalonePublicFlowPaths)[number]
  );

  // Invite emails land on /rower/invitations?token= before login; hide portal chrome until authed.
  const isRowerInviteEntry =
    location.pathname === '/rower/invitations' && new URLSearchParams(location.search).has('token');
  const suppressRowerPortalForInvite = isRowerInviteEntry && (authLoading || !user);

  const isCoachRoute = location.pathname.startsWith('/coach');
  const isRowerRoute =
    location.pathname.startsWith('/rower') &&
    !isStandalonePublicFlow &&
    !suppressRowerPortalForInvite;
  const isParentRoute = location.pathname.startsWith('/parent') && !isStandalonePublicFlow;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAppRoute = isCoachRoute || isRowerRoute || isParentRoute || isAdminRoute;
  const notificationPersona = isCoachRoute
    ? 'coach'
    : isRowerRoute
      ? 'rower'
      : isParentRoute
        ? 'parent'
        : null;
  const notificationCenterPath = notificationPersona
    ? `/${notificationPersona}/notifications`
    : null;
  const isPrototypeRoute =
    (location.pathname.startsWith('/coach/prototypes/') &&
      location.pathname !== '/coach/prototypes') ||
    (location.pathname.startsWith('/rower/prototypes/') &&
      location.pathname !== '/rower/prototypes');
  const isAuthRoute =
    isStandalonePublicFlow ||
    suppressRowerPortalForInvite ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/reset-password');

  const publicPages = [
    '/',
    '/about',
    '/contact',
    '/pricing',
    '/privacy',
    '/privacy/data-subject-request',
    '/terms',
    '/cookies',
    '/coppa-rights',
  ];
  const isPublicPage = publicPages.includes(location.pathname);
  const shouldShowFooter =
    !isCoachRoute &&
    !isRowerRoute &&
    !isParentRoute &&
    !isAdminRoute &&
    (isPublicPage || isAuthRoute);

  React.useEffect(() => {
    window.Termly?.initialize?.();
  }, [location.pathname]);

  const urlParams = new URLSearchParams(window.location.search);
  const currentDashboardId = urlParams.get('dashboard');

  const showSidebar = isAppRoute && (!isMobileViewport || mobileDrawerOpen);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Mobile top bar */}
      {isMobileViewport && isAppRoute && (
        <div className="fixed top-0 left-0 right-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-3">
          <button
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Open menu"
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-6 w-6 text-gray-700" />
          </button>
          <Link to="/" className="flex items-center space-x-2">
            <Waves className="h-6 w-6 text-brand-logo-icon" />
            <span className="text-lg font-bold text-brand-logo-icon">GLYYD</span>
          </Link>
          {notificationPersona && notificationCenterPath && (
            <div className="ml-auto">
              <NotificationsBell
                persona={notificationPersona}
                centerPath={notificationCenterPath}
              />
            </div>
          )}
        </div>
      )}

      {/* Mobile backdrop */}
      {isMobileViewport && mobileDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileDrawerOpen(false)}
        />
      )}

      {(() => {
        const appBody = (
          <div className="flex flex-1 overflow-x-hidden">
            {showSidebar && isCoachRoute && (
              <CoachSidebar
                isCollapsed={isMobileViewport ? false : sidebarCollapsed}
                onToggle={
                  isMobileViewport
                    ? () => setMobileDrawerOpen(false)
                    : () => setSidebarCollapsed(!sidebarCollapsed)
                }
                activeTab={getActiveSection()}
                onTabChange={handleTabChange}
                currentDashboardId={currentDashboardId}
              />
            )}
            {showSidebar && isRowerRoute && (
              <RowerSidebar
                isCollapsed={isMobileViewport ? false : sidebarCollapsed}
                onToggle={
                  isMobileViewport
                    ? () => setMobileDrawerOpen(false)
                    : () => setSidebarCollapsed(!sidebarCollapsed)
                }
              />
            )}
            {showSidebar && isParentRoute && (
              <ParentSidebar
                isCollapsed={isMobileViewport ? false : sidebarCollapsed}
                onToggle={
                  isMobileViewport
                    ? () => setMobileDrawerOpen(false)
                    : () => setSidebarCollapsed(!sidebarCollapsed)
                }
              />
            )}
            {showSidebar && isAdminRoute && (
              <AdminSidebar
                isCollapsed={isMobileViewport ? false : sidebarCollapsed}
                onToggle={
                  isMobileViewport
                    ? () => setMobileDrawerOpen(false)
                    : () => setSidebarCollapsed(!sidebarCollapsed)
                }
              />
            )}
            <AppMain
              pathname={location.pathname}
              className={`flex-1 min-w-0 bg-gray-50 custom-scrollbar ${
                (isCoachRoute && !isPrototypeRoute) || isRowerRoute || isParentRoute || isAdminRoute
                  ? 'h-screen overflow-y-auto'
                  : ''
              } ${
                !isMobileViewport && isAppRoute
                  ? sidebarCollapsed
                    ? 'ml-16 transition-[margin-left] duration-300 ease-in-out'
                    : 'ml-72 transition-[margin-left] duration-300 ease-in-out'
                  : ''
              } ${isMobileViewport && isAppRoute ? 'pt-14' : ''} ${
                isPrototypeRoute ? 'overflow-hidden' : ''
              } ${
                isRowerRoute || isParentRoute || location.pathname.startsWith('/admin/settings')
                  ? 'px-4 sm:px-6 lg:px-8 py-6'
                  : ''
              }`}
            >
              {isAdminRoute && <MfaGraceBanner />}
              {children}
            </AppMain>
          </div>
        );

        return isCoachRoute || isAdminRoute ? (
          <SidebarDrillProvider>{appBody}</SidebarDrillProvider>
        ) : (
          appBody
        );
      })()}
      {shouldShowFooter && <Footer />}
    </div>
  );
};

export default AppLayout;
